import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import Checkbox from "@mui/material/Checkbox";
import TextField from "@mui/material/TextField";
import { useMemo, useState } from "react";
import {
  GroupHeader,
  GroupItems,
  SelectedPrimaryText,
  SelectedSecondaryText,
  SelectedValueContainer,
} from "./groupedAutocomplete.styles";

/**
 * Truncates text to a maximum length, adding ellipsis if needed
 */
const truncateText = (text: string, maxLength = 50): string => {
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength)}...`;
};

/**
 * Splits text into parts with highlight information based on search query
 */
const getHighlightedParts = (
  text: string,
  query: string,
): Array<{ text: string; highlight: boolean }> => {
  if (!query.trim()) {
    return [{ text, highlight: false }];
  }

  const lowerText = text.toLowerCase();
  const lowerQuery = query.toLowerCase();

  const parts: Array<{ text: string; highlight: boolean }> = [];
  let startIndex = 0;

  let index = lowerText.indexOf(lowerQuery, startIndex);
  while (index !== -1) {
    if (index > startIndex) {
      parts.push({
        text: text.slice(startIndex, index),
        highlight: false,
      });
    }

    parts.push({
      text: text.slice(index, index + query.length),
      highlight: true,
    });

    startIndex = index + query.length;
    index = lowerText.indexOf(lowerQuery, startIndex);
  }

  if (startIndex < text.length) {
    parts.push({
      text: text.slice(startIndex),
      highlight: false,
    });
  }

  return parts;
};

/**
 * Base structure for an autocomplete option.
 * All options must have a unique `id` and a `label` for display.
 */
export interface AutocompleteOption {
  /** Unique identifier for the option */
  id: string;
  /** Primary display text for the option */
  label: string;
}

/**
 * Configuration for optional grouping behavior.
 * When provided, options will be grouped under headers.
 */
export interface GroupConfig {
  /** Function to extract the group key from an option */
  getGroupKey: (option: AutocompleteOption) => string;
  /** Function to get the display label for a group */
  getGroupLabel: (groupKey: string) => string;
  /** Optional: Function to get secondary text shown below the primary label when selected */
  getSecondaryLabel?: (option: AutocompleteOption) => string;
}

export interface GroupedAutocompleteProps {
  /** Array of options to display in the autocomplete */
  options: AutocompleteOption[];
  /** Callback when the selected value changes */
  onChange: (value: AutocompleteOption | AutocompleteOption[] | null) => void;
  /** Currently selected value (single or multiple) */
  value?: AutocompleteOption | AutocompleteOption[] | null;
  /** Whether the autocomplete is disabled */
  disabled?: boolean;
  /** Label for the input field */
  label?: string;
  /** Placeholder text */
  placeholder?: string;
  /** Optional grouping configuration. If not provided, options are not grouped. */
  groupConfig?: GroupConfig;
  /** Enable multiple selection with checkboxes */
  multiple?: boolean;
  /** Disable portal rendering to keep dropdown within parent container */
  disablePortal?: boolean;
  alwaysOpen?: boolean;
  /** Text to display when no options are available */
  noOptionsText?: string;
  /** Disable the clear button to prevent clearing the selection */
  disableClearable?: boolean;
}

interface InternalOption extends AutocompleteOption {
  _groupKey?: string;
  _secondaryLabel?: string;
}

export const GroupedAutocomplete = ({
  options,
  onChange,
  value = null,
  disabled = false,
  label = "Select option *",
  placeholder = "Search...",
  groupConfig,
  multiple = false,
  disablePortal = false,
  alwaysOpen,
  noOptionsText = "No options",
  disableClearable = false,
}: GroupedAutocompleteProps) => {
  const [inputValue, setInputValue] = useState("");

  // Enrich options with computed group and secondary label data
  const enrichedOptions: InternalOption[] = options.map((opt) => ({
    ...opt,
    _groupKey: groupConfig?.getGroupKey(opt),
    _secondaryLabel: groupConfig?.getSecondaryLabel?.(opt),
  }));

  // Sort options by group key to prevent duplicate headers warning
  if (groupConfig) {
    enrichedOptions.sort((a, b) => {
      const groupA = a._groupKey || "";
      const groupB = b._groupKey || "";
      return groupA.localeCompare(groupB);
    });
  }

  const filter = createFilterOptions<InternalOption>();

  const filterOptions = (
    opts: InternalOption[],
    state: { inputValue: string; getOptionLabel: (option: InternalOption) => string },
  ): InternalOption[] => {
    const input = state.inputValue.trim().toLowerCase();
    if (!input) {
      return opts;
    }

    // Filter by option label
    const byLabel = filter(opts, state);

    // If grouping is enabled, also filter by group label
    if (groupConfig) {
      const matchingGroupKeys = new Set(
        opts
          .filter((option) => {
            if (!option._groupKey) return false;
            const groupLabel = groupConfig.getGroupLabel(option._groupKey);
            return groupLabel.toLowerCase().includes(input);
          })
          .map((option) => option._groupKey),
      );

      const byGroup = opts.filter((option) => matchingGroupKeys.has(option._groupKey));

      // Merge and deduplicate
      const seen = new Set<string>();
      const merged: InternalOption[] = [];

      [...byLabel, ...byGroup].forEach((option) => {
        if (!seen.has(option.id)) {
          seen.add(option.id);
          merged.push(option);
        }
      });

      // Sort merged results by group key to prevent duplicate headers
      merged.sort((a, b) => {
        const groupA = a._groupKey || "";
        const groupB = b._groupKey || "";
        return groupA.localeCompare(groupB);
      });

      return merged;
    }
    return byLabel;
  };

  const singleValue = !multiple && value && !Array.isArray(value) ? value : null;
  // Enriched copy of the selected option (carries the secondary label for the overlay)
  const selectedSingleOption = singleValue
    ? (enrichedOptions.find((opt) => opt.id === singleValue.id) ?? null)
    : null;

  // In single mode, MUI must receive a value whose identity only changes when the selection
  // changes. `enrichedOptions` is rebuilt on every render, so the enriched copy gets a new
  // identity on every keystroke. MUI treats that as a value change and resets the input to
  // the selected label, which made it impossible to search for a different option once one
  // was selected. Options are matched to this value by id (see `isOptionEqualToValue`).
  const selectedSingleId = selectedSingleOption?.id;
  const selectedSingleLabel = selectedSingleOption?.label;
  const stableSingleValue = useMemo<InternalOption | null>(
    () =>
      selectedSingleId !== undefined && selectedSingleLabel !== undefined
        ? { id: selectedSingleId, label: selectedSingleLabel }
        : null,
    [selectedSingleId, selectedSingleLabel],
  );

  const selectedOption = multiple
    ? Array.isArray(value)
      ? (value
          .map((v) => enrichedOptions.find((opt) => opt.id === v.id))
          .filter(Boolean) as InternalOption[])
      : []
    : stableSingleValue;

  // In single mode the selection is drawn as an overlay (label + secondary label) while the
  // input is untouched. As soon as the user types a search query the overlay is hidden so
  // the typed text is visible.
  const displaySelectedOption =
    selectedSingleOption && (inputValue === "" || inputValue === selectedSingleOption.label)
      ? selectedSingleOption
      : null;

  return (
    <Autocomplete
      multiple={multiple}
      open={alwaysOpen ? true : undefined}
      disableCloseOnSelect={multiple}
      disablePortal={disablePortal}
      disableClearable={disableClearable}
      options={enrichedOptions}
      groupBy={groupConfig ? (option) => option._groupKey || "" : undefined}
      getOptionLabel={(option) => option.label}
      isOptionEqualToValue={(option, selected) => option.id === selected.id}
      value={selectedOption}
      noOptionsText={noOptionsText}
      slotProps={{
        listbox: {
          style: { maxHeight: "300px" },
        },
      }}
      onChange={(_, newValue, reason) => {
        if (reason === "clear") {
          if (multiple) return;
          onChange(null);
          return;
        }

        if (multiple) {
          if (Array.isArray(newValue)) {
            onChange(newValue.map((v) => ({ id: v.id, label: v.label })));
          } else {
            onChange([]);
          }
        } else {
          if (newValue && !Array.isArray(newValue)) {
            onChange({ id: newValue.id, label: newValue.label });
          } else {
            onChange(null);
          }
        }
      }}
      inputValue={inputValue}
      onInputChange={(_, newInputValue, reason) => {
        if (multiple) {
          // In multiple mode, only typing and clearing change the search text, so it is
          // kept while items are selected/deselected (including via backspace)
          if (reason === "input" || reason === "clear") {
            setInputValue(newInputValue);
          }
          return;
        }
        // In single mode, follow MUI: typing updates the search text, while selecting an
        // option or leaving the field restores the selected label (discarding an
        // abandoned search)
        setInputValue(newInputValue);
      }}
      disabled={disabled}
      filterOptions={filterOptions}
      renderInput={(params) => {
        // In multiple mode, selected items are not displayed in the input field
        // (displaySelectedOption is null); they are shown as checked checkboxes instead
        return (
          <TextField
            {...params}
            label={label}
            placeholder={placeholder}
            InputProps={{
              ...params.InputProps,
              startAdornment: displaySelectedOption ? (
                <SelectedValueContainer>
                  <SelectedPrimaryText>
                    {truncateText(displaySelectedOption.label)}
                  </SelectedPrimaryText>
                  {displaySelectedOption._secondaryLabel && (
                    <SelectedSecondaryText>
                      {truncateText(displaySelectedOption._secondaryLabel)}
                    </SelectedSecondaryText>
                  )}
                </SelectedValueContainer>
              ) : null,
            }}
            slotProps={{
              htmlInput: {
                ...params.inputProps,
                onClick: (event: React.MouseEvent<HTMLInputElement>) => {
                  params.inputProps.onClick?.(event);
                  // While the selection overlay is shown, the input's own text is hidden, so
                  // select all of it: typing then starts a new search instead of editing
                  // (appending to) the invisible label of the current selection.
                  if (displaySelectedOption) {
                    event.currentTarget.select();
                  }
                },
                sx: {
                  color: displaySelectedOption ? "transparent" : "inherit",
                  caretColor: displaySelectedOption ? "transparent" : "inherit",
                },
              },
            }}
          />
        );
      }}
      renderGroup={
        groupConfig
          ? (params) => {
              const groupLabel = groupConfig.getGroupLabel(params.group);
              const truncatedLabel = truncateText(groupLabel.toUpperCase());
              const parts = getHighlightedParts(truncatedLabel, inputValue);
              return (
                <li key={params.key}>
                  <GroupHeader>
                    {parts.map((part, index) => (
                      <span
                        // biome-ignore lint/suspicious/noArrayIndexKey: highlight parts have no stable id
                        key={`${index}-${part.text}`}
                        style={{ fontWeight: part.highlight ? 700 : 600 }}
                      >
                        {part.text}
                      </span>
                    ))}
                  </GroupHeader>
                  <GroupItems>{params.children}</GroupItems>
                </li>
              );
            }
          : undefined
      }
      renderOption={(props, option, { selected }) => {
        const displayText = truncateText(option.label);
        const parts = getHighlightedParts(displayText, inputValue);
        return (
          <li {...props} key={option.id}>
            {multiple && <Checkbox checked={selected} sx={{ marginRight: 1 }} />}
            {parts.map((part, index) => (
              <span
                // biome-ignore lint/suspicious/noArrayIndexKey: highlight parts have no stable id
                key={`${index}-${part.text}`}
                style={{ fontWeight: part.highlight ? 700 : 400 }}
              >
                {part.text}
              </span>
            ))}
          </li>
        );
      }}
    />
  );
};
